(() => {
  if (customElements.get('kukai-homepage')) return;

  class KukaiHomepage extends HTMLElement {
    connectedCallback() {
      if (this.events) return;
      window.kukaiHomepageController?.destroy();
      this.events = new AbortController();
      this.destroyed = false;
      this.frame = 0;
      this.banner = this.querySelector('.kukai-banner');
      if (this.banner) {
        this.bannerAnchor = document.createComment('Original desktop banner position');
        this.banner.before(this.bannerAnchor);
      }
      this.mobile = window.matchMedia('(max-width: 749px)');
      document.documentElement.classList.add('kukai-homepage-active');
      this.observer = new ResizeObserver(() => this.scheduleColour());
      this.sizeHeroBound = () => this.sizeHero();
      this.controller = { destroy: () => this.destroy() };
      window.kukaiSizeHero = this.sizeHeroBound;
      window.kukaiHomepageController = this.controller;
      const options = { signal: this.events.signal };
      const refresh = () => this.refresh();
      window.addEventListener('scroll', () => this.scheduleColour(), { ...options, capture: true, passive: true });
      window.addEventListener('resize', refresh, options);
      window.addEventListener('load', refresh, options);
      document.addEventListener('DOMContentLoaded', refresh, options);
      document.addEventListener('shopify:section:load', refresh, options);
      document.addEventListener('shopify:section:reorder', refresh, options);
      document.addEventListener('shopify:section:unload', () => requestAnimationFrame(refresh), options);
      document.addEventListener('kukai:menu-change', () => this.updateColour(), options);
      this.refresh();
    }

    layoutBanner() {
      if (!this.banner) return;
      const products = document.querySelector('.kukai-products');
      const productSection = products?.closest('[id^="shopify-section-"]') || products?.closest('.kukai-home');
      if (this.mobile.matches && productSection) {
        if (!this.bannerHost) {
          this.bannerHost = document.createElement('div');
          this.bannerHost.className = 'kukai-home kukai-home--mobile-banner';
        }
        if (productSection.nextElementSibling !== this.bannerHost) productSection.after(this.bannerHost);
        if (this.banner.parentElement !== this.bannerHost) this.bannerHost.append(this.banner);
      } else {
        this.restoreBanner();
      }
    }

    restoreBanner() {
      if (this.bannerAnchor?.parentNode && this.bannerAnchor.nextSibling !== this.banner) {
        this.bannerAnchor.after(this.banner);
      }
      this.bannerHost?.remove();
      this.bannerHost = null;
    }

    sizeHero() {
      const hero = this.querySelector('.kukai-hero');
      if (!hero || this.destroyed) return;
      let documentTop = hero.getBoundingClientRect().top + window.scrollY;
      for (let parent = hero.parentElement; parent; parent = parent.parentElement) {
        if (parent !== document.scrollingElement) documentTop += parent.scrollTop;
      }
      const height = this.mobile.matches ? 480 : Math.max(window.innerHeight - documentTop - 33, 300);
      const value = `${height}px`;
      if (hero.style.height !== value) hero.style.height = value;
    }

    getColourBoundary() {
      // On mobile the header reaches a white surface immediately after the hero.
      if (this.mobile.matches) return this.querySelector('.kukai-hero');
      return this.querySelector('.kukai-banner')
        || this.querySelector('.kukai-cards')?.lastElementChild
        || this.querySelector('.kukai-hero');
    }

    clearMobileHeaderStyles(header) {
      for (const property of [
        '--kukai-mobile-header-progress',
        '--kukai-mobile-header-top',
        '--kukai-mobile-header-backdrop-top',
        '--kukai-mobile-header-padding-y',
        '--kukai-home-menu-colour'
      ]) header.style.removeProperty(property);
    }

    updateColour() {
      this.frame = 0;
      if (this.destroyed) return;
      window.kukaiUpdateHeaderTop?.();
      const imagery = this.getColourBoundary();
      const boundary = imagery ? imagery.getBoundingClientRect().bottom : Infinity;
      const menuOpen = this.mobile.matches && document.getElementById('kukai-mobile-menu')
        ?.classList.contains('kukai-mobile-menu--open');
      const announcement = document.getElementById('kukai-announcement');
      const announcementRect = announcement && !announcement.hidden
        ? announcement.getBoundingClientRect() : null;
      document.querySelectorAll('.kukai-header--transparent').forEach(header => {
        let dark;
        if (this.mobile.matches) {
          const hasAnnouncement = announcementRect && announcementRect.height > 0;
          // Without an announcement, start transparent at the page top and use
          // actual scroll distance instead of treating the bar as already gone.
          const bottom = hasAnnouncement ? announcementRect.bottom : -Math.max(0, window.scrollY);
          const initialBottom = hasAnnouncement
            ? announcementRect.height + (parseFloat(getComputedStyle(announcement).marginTop) || 0)
            : 0;
          // Keep the same 38px colour transition when the bar is dismissed.
          const travel = hasAnnouncement ? Math.max(1, initialBottom) : 38;
          const linearProgress = Math.max(0, Math.min(1, (initialBottom - bottom) / travel));
          const progress = menuOpen ? 1 : linearProgress * linearProgress * (3 - 2 * linearProgress);
          const remaining = bottom + 12;
          // Follow the page at 1:1 speed, then slow down across a 12px settling range.
          // The derivative stays between 0 and 1: the header never outruns the scroll.
          const settleRadius = 6;
          const top = menuOpen ? 0 : remaining >= settleRadius ? remaining
            : remaining <= -settleRadius ? 0
            : Math.pow(remaining + settleRadius, 2) / (4 * settleRadius);
          const shade = Math.round(255 - 138 * progress);
          header.style.setProperty('--kukai-mobile-header-progress', String(progress));
          header.style.setProperty('--kukai-mobile-header-top', top + 'px');
          header.style.setProperty('--kukai-mobile-header-backdrop-top', (-Math.min(12, top)) + 'px');
          header.style.setProperty('--kukai-mobile-header-padding-y', '18px');
          header.style.setProperty('--kukai-home-menu-colour', 'rgb(' + shade + ', ' + shade + ', ' + shade + ')');
          dark = progress >= 0.5;
        } else {
          this.clearMobileHeaderStyles(header);
          const reference = (header.querySelector('.kukai-header__logo') || header).getBoundingClientRect();
          dark = boundary <= reference.top + reference.height / 2;
        }
        header.querySelector('.kukai-header__logo-img--light')?.setAttribute('aria-hidden', String(dark));
        header.querySelector('.kukai-header__logo-img--dark')?.setAttribute('aria-hidden', String(!dark));
        header.classList.toggle('kukai-header--dark', dark);
        header.setAttribute('data-kukai-home-tone', dark ? 'dark' : 'light');
      });
    }

    scheduleColour() {
      if (!this.destroyed && !this.frame) this.frame = requestAnimationFrame(() => this.updateColour());
    }

    refresh() {
      if (this.destroyed) return;
      this.layoutBanner();
      this.sizeHero();
      this.observer.disconnect();
      const boundary = this.getColourBoundary();
      if (boundary) this.observer.observe(boundary);
      this.observer.observe(this);
      const announcement = document.getElementById('kukai-announcement');
      if (announcement) this.observer.observe(announcement);
      document.querySelectorAll('.kukai-header--transparent').forEach(header => this.observer.observe(header));
      this.updateColour();
    }

    destroy() {
      if (this.destroyed) return;
      this.destroyed = true;
      cancelAnimationFrame(this.frame);
      this.observer?.disconnect();
      this.events?.abort();
      this.events = null;
      this.restoreBanner();
      this.bannerAnchor?.remove();
      document.documentElement.classList.remove('kukai-homepage-active');
      document.querySelectorAll('.kukai-header[data-kukai-home-tone]').forEach(header => {
        this.clearMobileHeaderStyles(header);
        header.querySelectorAll('.kukai-header__logo-img').forEach(image => image.removeAttribute('aria-hidden'));
        header.removeAttribute('data-kukai-home-tone');
        header.classList.remove('kukai-header--dark', 'kukai-header--scrolled');
      });
      if (window.kukaiHomepageController === this.controller) delete window.kukaiHomepageController;
      if (window.kukaiSizeHero === this.sizeHeroBound) delete window.kukaiSizeHero;
    }

    disconnectedCallback() { this.destroy(); }
  }
  customElements.define('kukai-homepage', KukaiHomepage);
})();